import { useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TableSortLabel, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { QueryBucket, QueryRow, SeoReport } from './seoTypes';
import { queryPrevImpr } from './seoCompute';
import { DeltaCell, Sparkline, fmtInt, fmtPos, pct } from './seoFormat';

interface Props {
    report: SeoReport;
    sel: number;
}

type QCol = 'q' | 'theme' | 'page' | 'impr' | 'clicks' | 'ctr' | 'position' | 'mom';

const TEXT_COLS: QCol[] = ['q', 'theme', 'page'];

const COLUMNS: { key: QCol; label: string; align: 'left' | 'right' }[] = [
    { key: 'q', label: 'Query', align: 'left' },
    { key: 'theme', label: 'Theme', align: 'left' },
    { key: 'page', label: 'Top page', align: 'left' },
    { key: 'impr', label: 'Impressions', align: 'right' },
    { key: 'clicks', label: 'Clicks', align: 'right' },
    { key: 'ctr', label: 'CTR', align: 'right' },
    { key: 'position', label: 'Avg pos', align: 'right' },
];

function qVal(report: SeoReport, r: QueryRow, col: QCol, i: number): number | string {
    if (col === 'q' || col === 'theme' || col === 'page') return r[col] || '';
    if (col === 'ctr') return r.impr[i] ? r.clicks[i] / r.impr[i] : 0;
    if (col === 'position') return r.pos[i] > 0 ? r.pos[i] : Infinity;
    if (col === 'mom') {
        const pv = queryPrevImpr(report, r, i);
        return pv ? (pct(r.impr[i], pv) as number) : -Infinity;
    }
    return r[col][i];
}

const QueryTable = ({ report, bucket, sel }: { report: SeoReport; bucket: QueryBucket; sel: number }) => {
    const [col, setCol] = useState<QCol>('impr');
    const [dir, setDir] = useState<'asc' | 'desc'>('desc');

    const rows = bucket.rows.slice().sort((a, b) => {
        const av = qVal(report, a, col, sel);
        const bv = qVal(report, b, col, sel);
        if (TEXT_COLS.includes(col)) {
            const as = String(av).toLowerCase();
            const bs = String(bv).toLowerCase();
            return dir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
        }
        const an = Number.isNaN(av as number) ? -Infinity : (av as number);
        const bn = Number.isNaN(bv as number) ? -Infinity : (bv as number);
        return dir === 'asc' ? an - bn : bn - an;
    });

    const onSort = (c: QCol) => {
        if (col === c) setDir(dir === 'asc' ? 'desc' : 'asc');
        else {
            setCol(c);
            setDir('desc');
        }
    };

    const dataCells = (r: QueryRow, bold?: boolean) => {
        const ctr = r.impr[sel] ? r.clicks[sel] / r.impr[sel] : 0;
        const pv = queryPrevImpr(report, r, sel);
        const mom = pv != null ? pct(r.impr[sel], pv) : null;
        const fw = bold ? 700 : 400;
        return (
            <>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: fw }}>{fmtInt(r.impr[sel])}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: fw }}>{fmtInt(r.clicks[sel])}</TableCell>
                <TableCell align="right" sx={{ fontWeight: fw }}>{`${(ctr * 100).toFixed(2)}%`}</TableCell>
                <TableCell align="right" sx={{ fontWeight: fw }}>{fmtPos(r.pos[sel])}</TableCell>
                <TableCell><Sparkline data={r.impr} /></TableCell>
                <TableCell align="right" sx={{ fontWeight: fw }}><DeltaCell p={mom} /></TableCell>
            </>
        );
    };

    const subtotalRow: QueryRow = {
        q: 'Subtotal',
        theme: '',
        page: '',
        impr: bucket.subtotal.impr,
        clicks: bucket.subtotal.clicks,
        pos: bucket.subtotal.pos,
        pmtd: bucket.subtotal.pmtd,
    };

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 920 }}>
                <TableHead>
                    <TableRow>
                        {COLUMNS.map((c) => (
                            <TableCell key={c.key} align={c.align} sortDirection={col === c.key ? dir : false}>
                                <TableSortLabel active={col === c.key} direction={col === c.key ? dir : 'desc'} onClick={() => onSort(c.key)}>
                                    {c.label}
                                </TableSortLabel>
                            </TableCell>
                        ))}
                        <TableCell>Trend</TableCell>
                        <TableCell align="right" sortDirection={col === 'mom' ? dir : false}>
                            <TableSortLabel active={col === 'mom'} direction={col === 'mom' ? dir : 'desc'} onClick={() => onSort('mom')}>
                                Impr MoM
                            </TableSortLabel>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow key={r.q} hover>
                            <TableCell sx={{ maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.q}>{r.q}</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: 12.5, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.theme}>{r.theme}</TableCell>
                            <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.page}>{r.page}</TableCell>
                            {dataCells(r)}
                        </TableRow>
                    ))}
                    <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', bgcolor: '#fafafa' } }}>
                        <TableCell sx={{ fontWeight: 700 }}>Subtotal</TableCell>
                        <TableCell />
                        <TableCell />
                        {dataCells(subtotalRow, true)}
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export const SeoQueries = ({ report, sel }: Props) => {
    if (!report.queries || report.queries.length === 0) return null;
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Bilingual (EN/FR) keyword rules classify each query by intent; unmatched queries default to Informational →
                &quot;Other (unmatched)&quot;. <b>Top page</b> is the page each query most lands on. GSC anonymizes rare queries,
                so totals here are lower than the page totals above.
            </Typography>
            {report.queries.map((bucket, qi) => (
                <Accordion key={bucket.name} defaultExpanded={qi === 0} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {bucket.name}{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                                · {bucket.nqueries} quer{bucket.nqueries !== 1 ? 'ies' : 'y'}
                                {bucket.dropped > 0 ? ` · top ${bucket.rows.length} shown` : ''}
                            </Typography>
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <QueryTable report={report} bucket={bucket} sel={sel} />
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};
